/**
 * P3-38: TimeUpgradeBtn —— Chronoforge / VoidSpace 升级按钮。
 *
 * 复用 StackableButton 能力但支持 CFU 特殊名称（delayTicks / heat）
 * 与 VSU 的 cryochambers (on/val) 显示。
 *
 * 对齐 `js/time.js:1624-1751`。
 */
import { useCallback, useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";
import { StackableButton } from "../../components/StackableButton";
import type { StackableButtonController, StackableButtonSnapshot } from "../../components/StackableButton";
import { useButtonModel, shallowSnapEq, makeModernTooltip, type ModernButtonController } from "../bonfire/useButtonModel";

type TimeUpgradeType = "chronoforge" | "voidSpace";

interface TimeUpgradeBtnProps {
  id: string;
  type: TimeUpgradeType;
}

interface TimeMeta {
  name: string;
  label: string;
  description?: string;
  val?: number;
  on?: number;
  heat?: number;
  delayTicks?: number;
  isAutomationEnabled?: boolean | null;
  togglable?: boolean;
  hasSellLink?: boolean;
  showSellLink?: boolean;
  lackResConvert?: boolean;
}

function buildName(meta: TimeMeta, game: GamePage): string {
  const label = meta.label ?? meta.name;
  if (meta.delayTicks != null && meta.delayTicks > 0) {
    const timeStr = meta.isAutomationEnabled
      ? String.fromCodePoint(0x1f550 + meta.delayTicks - 1)
      :
          game.getDisplayValueExt(
            meta.delayTicks / game.ticksPerSecond,
            false,
            false,
            1,
          ) +
          t("unit.s");
    return timeStr + " " + label;
  }
  if (meta.heat != null && meta.heat > 0) {
    return (
      label +
      "<div class=\"progress\">[" +
      game.getDisplayValueExt(meta.heat) +
      "%]</div>"
    );
  }
  if (meta.name === "cryochambers" && meta.on != null && meta.val != null && meta.on !== meta.val) {
    return label + " (" + meta.on + "/" + meta.val + ")";
  }
  return label;
}

function toSnapshot(model: Record<string, unknown>, game: GamePage): StackableButtonSnapshot {
  const meta = (model.metadata ?? {}) as TimeMeta;
  const onOffLink = model.togglableOnOffLink as { title?: string; cssClass?: string; tooltip?: string; handler?: () => void } | undefined;
  const autoLink = model.toggleAutomationLink as { title?: string; cssClass?: string; tooltip?: string; handler?: () => void } | undefined;
  const val = meta.val ?? 0;
  const on = meta.on ?? 0;
  return {
    name: buildName(meta, game),
    description: String(model.description ?? ""),
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    val,
    on,
    metaName: meta.name ?? "",
    lackResConvert: meta.lackResConvert,
    togglable: !!model.togglable,
    hasSellLink: !!model.hasSellLink,
    showSellLink: !!model.showSellLink,
    togglableOnOffLink: onOffLink
      ? {
          title: onOffLink.title ?? "",
          cssClass: onOffLink.cssClass,
          tooltip: onOffLink.tooltip,
          handler: (event: MouseEvent) => {
            event.stopPropagation();
            onOffLink.handler?.();
          },
        }
      : undefined,
    toggleAutomationLink: autoLink
      ? {
          title: autoLink.title ?? "",
          cssClass: autoLink.cssClass,
          tooltip: autoLink.tooltip,
          handler: (event: MouseEvent) => {
            event.stopPropagation();
            autoLink.handler?.();
          },
        }
      : undefined,
  };
}

function snapEq(a: StackableButtonSnapshot | null, b: StackableButtonSnapshot | null): boolean {
  return shallowSnapEq(a, b);
}

export default function TimeUpgradeBtn({ id, type }: TimeUpgradeBtnProps) {
  const controller = useMemo<StackableButtonController | null>(() => {
    const game = getGame();
    if (!game) {
      return null;
    }
    const ctor = (
      window as unknown as {
        classes?: {
          ui?: {
            time?: {
              ChronoforgeBtnController?: new (game: GamePage) => StackableButtonController;
              VoidSpaceBtnController?: new (game: GamePage) => StackableButtonController;
            };
          };
        };
      }
    ).classes?.ui?.time?.[type === "chronoforge" ? "ChronoforgeBtnController" : "VoidSpaceBtnController"];
    if (typeof ctor !== "function") {
      return null;
    }
    return new ctor(game);
  }, [type]);

  const createOpts = useCallback(
    (game: GamePage) => ({
      id,
      name: type === "chronoforge" ? (game.time?.getCFU?.(id)?.label ?? id) : (game.time?.getVSU?.(id)?.label ?? id),
      description: "",
    }),
    [id, type],
  );

  const { snap, modelRef } = useButtonModel(
    controller as ModernButtonController | null,
    createOpts,
    (model) => {
      const game = getGame();
      return toSnapshot(model, game as GamePage);
    },
    snapEq,
  );

  const getTooltip = useMemo(() => makeModernTooltip(() => controller as ModernButtonController | null, createOpts), [controller, createOpts]);

  const getModel = useCallback(() => modelRef.current, [modelRef]);

  if (!snap || !controller) {
    return null;
  }

  return (
    <StackableButton
      snap={snap}
      controller={controller}
      getModel={getModel}
      getTooltip={getTooltip}
      renderAfterBuy={true}
    />
  );
}
